/**
 * 문서 직렬화 (P0-⑤ 데이터 파트): blocks[] ↔ 에디터 DOM(.editor 컨테이너).
 * 모든 직렬화 모듈을 결합하고 fontSize 형제키 lift/lower를 문서 경계에서 적용한다.
 *
 *  - blocksToDom(blocks, container): 저장 blocks(형제키 형태) → liftFontSize → DOM
 *  - domToBlocks(container):         DOM → blocks → lowerFontSize(저장 형태)
 *
 * 라운드트립: domToBlocks(blocksToDom(fixture)) === fixture  (EC1/EC2/EC3)
 *
 * 표 블록: 라이브 DOM 요소에 `__model`(표 모델)을 보관(샘플 방식). 표 블록 props/children은
 *   현재 verbatim 보존(__props/__children) — 표 DOM 렌더(renderTable)와 props의 DOM attr
 *   매핑(tableAlignment 등)은 P3에서 결합한다.
 */

import { blockToElement, elementToBlock } from "./block.js";
import { modelToTableContent, tableContentToModel } from "./table.js";
import { liftFontSize, lowerFontSize } from "./font-size.js";
import { renderTableElement, syncModelFromTableDom } from "../table/render.js";
import { registry } from "../features/index.js"; // 기능 슬라이스(스펙 레지스트리) — 커스텀 블록 직렬화 합성

const EMPTY_TABLE_CONTENT = { type: "tableContent", columnWidths: [], rows: [] };

function tableBlockToElement(block) {
  if (typeof document === "undefined") throw new Error("tableBlockToElement: DOM(document) 필요");
  const el = document.createElement("div");
  el.className = "block";
  el.dataset.type = "table";
  el.dataset.contentType = "table"; // BlockNote data-content-type 별칭(소비처 CSS 호환)
  if (block.id != null) el.dataset.id = String(block.id);
  el.__props = block.props ? { ...block.props } : undefined; // verbatim 보존(P3에서 DOM attr 매핑)
  if (block.props && block.props.tableAlignment && block.props.tableAlignment !== "left") el.dataset.tableAlignment = block.props.tableAlignment; // 표 전체 정렬(F1)
  el.__model = tableContentToModel(block.content || EMPTY_TABLE_CONTENT);
  el.__children = Array.isArray(block.children) ? block.children : [];
  const content = document.createElement("div");
  content.className = "content";
  content.setAttribute("contenteditable", "false");
  content.appendChild(renderTableElement(el.__model)); // 라이브 <table> 렌더
  el.appendChild(content);
  return el;
}

function tableElementToBlock(el) {
  const tableEl = el.querySelector("table.lumir-tbl");
  if (tableEl) syncModelFromTableDom(tableEl); // 라이브 셀 편집 → 모델 회수
  const block = {};
  if (el.dataset.id != null) block.id = el.dataset.id;
  block.type = "table";
  if (el.__props !== undefined) block.props = el.__props;
  block.content = modelToTableContent(el.__model);
  block.children = el.__children || [];
  return block;
}

// ── 멀티컬럼 (P4): columnList > column > 중첩 블록(편집 가능) ──
const COL_PARA = () => ({ type: "paragraph", props: { textColor: "default", backgroundColor: "default", textAlignment: "left" }, content: [], children: [] });

/** 컬럼 사이 너비 리사이즈 핸들(P9-⑦) */
function makeColResizeHandle() {
  const h = document.createElement("div");
  h.className = "lumir-col-resize";
  h.setAttribute("contenteditable", "false");
  return h;
}

function columnToElement(col) {
  const c = document.createElement("div");
  c.className = "lumir-column";
  c.dataset.type = "column";
  if (col && col.id != null) c.dataset.id = String(col.id);
  c.__props = col && col.props ? { ...col.props } : undefined;
  const w = col && col.props && col.props.width;
  c.style.flexGrow = String(w || 1);
  c.style.flexShrink = "1";
  c.style.flexBasis = "0";
  if (w != null && w !== 1) c.dataset.columnWidth = String(w);
  const kids = col && Array.isArray(col.children) ? col.children : [];
  for (const b of kids) c.appendChild(blockToEl(b));
  if (!c.querySelector(":scope > .block")) c.appendChild(blockToEl(COL_PARA())); // 빈 컬럼 방지
  return c;
}
function columnListBlockToElement(block) {
  const el = document.createElement("div");
  el.className = "block";
  el.dataset.type = "columnList";
  el.dataset.contentType = "columnList"; // BlockNote data-content-type 별칭(소비처 CSS 호환)
  if (block.id != null) el.dataset.id = String(block.id);
  el.__props = block.props ? { ...block.props } : undefined;
  const content = document.createElement("div");
  content.className = "content lumir-columns";
  if (block.props && block.props.showDivider) content.classList.add("with-divider"); // 블록별 구분선
  content.setAttribute("contenteditable", "false");
  // 2단까지만 지원: 3단 이상(레거시/외부 데이터)은 초과 컬럼의 블록을 2번째 컬럼으로 병합.
  let cols = Array.isArray(block.children) ? block.children : [];
  if (cols.length > 2) {
    const second = cols[1] || { type: "column", props: { width: 1 }, children: [] };
    const merged = { ...second, children: [...(second.children || []), ...cols.slice(2).flatMap((c) => (c && c.children) || [])] };
    cols = [cols[0], merged];
  }
  cols.forEach((col, i) => {
    content.appendChild(columnToElement(col));
    if (i < cols.length - 1) content.appendChild(makeColResizeHandle());
  });
  el.appendChild(content);
  return el;
}
function columnElementToBlock(c) {
  const col = {};
  if (c.dataset.id != null) col.id = c.dataset.id;
  col.type = "column";
  const w = c.dataset.columnWidth ? parseFloat(c.dataset.columnWidth) : (c.__props && c.__props.width) || 1;
  col.props = { ...(c.__props || {}), width: Number.isFinite(w) ? w : 1 };
  col.children = [...c.children].filter((b) => b.classList && b.classList.contains("block")).map(elToBlock);
  return col;
}
function columnListElementToBlock(el) {
  const block = {};
  if (el.dataset.id != null) block.id = el.dataset.id;
  block.type = "columnList";
  block.props = el.__props !== undefined ? el.__props : {};
  block.children = [...el.querySelectorAll(":scope > .content > .lumir-column")].map(columnElementToBlock);
  return block;
}

const CUSTOM_TO_EL = {
  table: tableBlockToElement,
  columnList: columnListBlockToElement,
  ...registry.toEl, // 이전된 기능 슬라이스: pageBreak/image/video/audio/file 등
};
const CUSTOM_FROM_EL = {
  table: tableElementToBlock,
  columnList: columnListElementToBlock,
  ...registry.fromEl, // 이전된 기능 슬라이스: pageBreak/image/video/audio/file 등
};

/**
 * 커스텀(비제네릭) 블록 타입 집합 — table/columnList + 이전된 기능 슬라이스(image/video/audio/file/bookmark/pageBreak 등).
 * 붙여넣기 정제(화이트리스트)의 권위 소스: 기능이 레지스트리로 이전돼도 자동 동기화된다.
 */
export const CUSTOM_BLOCK_TYPES = new Set(Object.keys(CUSTOM_TO_EL));

function blockToEl(block) {
  const fn = block && CUSTOM_TO_EL[block.type];
  const el = fn ? fn(block) : blockToElement(block);
  // 중첩 children(들여쓰기/하위 리스트) — 커스텀(table/columnList 등)은 자체 처리, 일반 블록만 .lumir-children로 렌더
  if (!fn && block && Array.isArray(block.children) && block.children.length) {
    const kids = document.createElement("div");
    kids.className = "lumir-children";
    for (const c of block.children) kids.appendChild(blockToEl(c));
    el.appendChild(kids);
  }
  return el;
}

/** 단일 블록 → DOM 요소(타입별 라우팅). 에디터의 프로그램적 블록 삽입용. */
export function renderBlock(block) {
  return blockToEl(block);
}

export function elToBlock(el) {
  const fn = CUSTOM_FROM_EL[el.dataset.type];
  const block = fn ? fn(el) : elementToBlock(el);
  if (!fn) {
    // 중첩 children은 DOM(.lumir-children)에서 읽어 편집 내용 반영 (없으면 [])
    const kidsEl = el.querySelector(":scope > .lumir-children");
    block.children = kidsEl ? [...kidsEl.children].filter((b) => b.classList && b.classList.contains("block")).map(elToBlock) : [];
  }
  return block;
}

/** 저장 blocks → 에디터 DOM 컨테이너(.block 요소들). fontSize lift 적용. */
export function blocksToDom(blocks, container) {
  if (typeof document === "undefined") throw new Error("blocksToDom: DOM(document) 필요");
  const lifted = liftFontSize(Array.isArray(blocks) ? blocks : []);
  const root = container || document.createElement("div");
  root.innerHTML = "";
  for (const b of lifted) root.appendChild(blockToEl(b));
  return root;
}

/** 에디터 DOM 컨테이너 → 저장 blocks. fontSize lower 적용(저장 형태). */
export function domToBlocks(container) {
  const out = [];
  const children = container ? container.children : [];
  for (const el of children) {
    if (el.classList && el.classList.contains("block")) out.push(elToBlock(el));
  }
  return lowerFontSize(out);
}
